import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, X } from "lucide-react";

interface JackpotFiltersProps {
  search: string;
  onSearchChange: (v: string) => void;
  stateFilter: string;
  onStateFilterChange: (v: string) => void;
  gameFilter: string;
  onGameFilterChange: (v: string) => void;
  dateFilter: string;
  onDateFilterChange: (v: string) => void;
  states: Array<{ id: string; name: string; abbreviation: string }>;
  games: Array<{ name: string }>;
  hasActiveFilters: boolean;
  onClear: () => void;
}

export function JackpotFilters({
  search, onSearchChange,
  stateFilter, onStateFilterChange,
  gameFilter, onGameFilterChange,
  dateFilter, onDateFilterChange,
  states, games,
  hasActiveFilters, onClear,
}: JackpotFiltersProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center gap-2 mb-4">
      <div className="relative w-full lg:flex-1 lg:min-w-[160px] lg:max-w-xs">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          aria-label="Search games"
          placeholder="Search games…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-8 h-9"
        />
      </div>

      <Select value={stateFilter} onValueChange={onStateFilterChange}>
        <SelectTrigger className="w-full lg:w-[140px] h-9" aria-label="Filter by state">
          <SelectValue placeholder="All States" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All States</SelectItem>
          {states.map((s) => (
            <SelectItem key={s.id} value={s.abbreviation}>{s.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={gameFilter} onValueChange={onGameFilterChange}>
        <SelectTrigger className="w-full lg:w-[160px] h-9" aria-label="Filter by game">
          <SelectValue placeholder="All Games" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Games</SelectItem>
          {[...new Set(games.map(g => g.name))].sort().map((name) => (
            <SelectItem key={name} value={name}>{name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={dateFilter} onValueChange={onDateFilterChange}>
        <SelectTrigger className="w-full lg:w-[160px] h-9" aria-label="Filter by draw date">
          <SelectValue placeholder="Any Draw Date" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Any Draw Date</SelectItem>
          <SelectItem value="today">Drawing Today</SelectItem>
          <SelectItem value="week">Drawing This Week</SelectItem>
        </SelectContent>
      </Select>

      {hasActiveFilters && (
        <Button variant="ghost" size="sm" onClick={onClear} className="h-9 col-span-full lg:col-auto">
          <X className="h-4 w-4 mr-1" /> Clear
        </Button>
      )}
    </div>
  );
}
